import {Lang, langEn, langRu} from 'yougile-lang'
import {ArrUtil, Time, TypeUtil, Util} from 'yougile-util'
import {
  ColumnColor,
  ColumnCrmSettings,
  createDataObjectId,
  crmDealAmountStickerId,
  customStickerTypes,
  DataObjectId,
  EntitledHubLike,
  getColumnColorNumber,
  Hub,
  HubLike,
  JSONLikeValue,
  SandboxTree,
  StorageDepartmentHierarchy,
  Tag,
  TaskData,
  taskTemplatesContainerId,
  TimerStickerEntry
} from 'yougile-model'
import {Items} from '@api/model/items'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {Gantt} from '@extensions/gantt'
import {GanttDraft} from '@extensions/gantt-draft'
import {ChatSubscribers} from './chat-subscribers'
import {Column, ColumnHub} from './column'
import {globals} from './globals-base'
import {Order} from './order'
import {Project} from './project'
import {SeenBy} from './seen-by'
import {Sticker} from './sticker/sticker'
import {Task} from './task'
import {taskDataFromHub} from './task-data-extractor'
import {filterCustomStickers} from './task-templates/filter-custom-stickers'
import {recreateSubtasksForTaskTemplate} from './task-templates/recreate-subtasks-for-task-template'

export class Board {
  /**
   * board id -> order
   */
  private static columnsOrder: Map<DataObjectId, Order> = new Map()

  public static clean(): void {
    for (const order of Board.columnsOrder.values()) {
      order.cleanup()
    }
    Board.columnsOrder.clear()
  }

  public static getColumnsOrder(board: HubLike): Order {
    if (!Board.columnsOrder.has(board.id)) {
      const order = new Order(board.companyId)
      order.load(board.id, board.id, Sync.get(board).getChildrenHubs(board.id))
      Board.columnsOrder.set(board.id, order)
    }
    return Board.columnsOrder.get(board.id)
  }

  public static addColumnsMirror(columns: Hub[], board: Hub): Hub {
    const box = Sync.get(board)
    const title = columns[0]?.title ?? Lang.msg.mirrorColumnDefaultTitle
    const newCol = new Hub(board.companyId, title)
    box.add(newCol, board.id, true, false)
    Board.getColumnsOrder(board).addEntity(newCol, true)
    const tag = new Tag(board.companyId, '.mirror')
    box.add(tag, newCol.id)
    columns.forEach((col: Hub) => {
      box.connect(tag.id, col.id)
    })
    return newCol
  }

  public static addColumn(
    title: string,
    board: Hub,
    allowApi = false,
    options?: {color?: ColumnColor; crmSettings?: ColumnCrmSettings}
  ): Hub {
    title = title.trim()

    if (allowApi && globals.getCompanyId() === board.companyId) {
      if (!Items.beforeAdd(Items.fromHub(board), title)) {
        return undefined
      }
    }
    const newCol = new Hub(board.companyId, title)
    const columns = Board.listColumns(board)

    newCol.data.columnColor =
      options?.color ?? getColumnColorNumber(newCol, columns)

    if (options?.crmSettings) {
      newCol.data.crmSettings = options.crmSettings
    }

    Sync.get(board).add(newCol, board.id, true, false)
    Board.getColumnsOrder(board).addEntity(newCol, true)

    if (allowApi && globals.getCompanyId() === board.companyId) {
      Items.add(Items.fromHub(newCol))
    }
    return newCol
  }

  public static duplicate(
    board: Hub,
    toProject?: Hub,
    oldNewMap?: Map<DataObjectId, DataObjectId>
  ): void {
    const localOldNewMap = new Map<DataObjectId, DataObjectId>()
    const box = Sync.get(board)
    const project = toProject || (box.get(board.location) as Hub)
    const title = box.notConflictingTitle(board.title, 'Hub', project.id)
    const dup = Project.addBoard(title, project, false, true)
    if (!dup) {
      return
    }
    localOldNewMap.set(board.id, dup.id)
    if (board.data) {
      dup.data = Util.clone(board.data)
    }
    if (board.properties) {
      dup.properties = Util.clone(board.properties)
    }
    dup.description = board.description
    if (board.stickers) {
      dup.stickers = Util.clone(board.stickers)
    }
    const ord = Project.getBoardsOrder(project)
    const ix = ord.order(board) + 1
    ord.handleDrop(ix, dup)

    this.recreateTaskTemplates(dup)

    dup.commit()

    const columns = Board.listColumns(board)
    Board.sortColumns(board, columns)
    columns.forEach(t => Column.duplicate(t, dup, localOldNewMap))

    if (oldNewMap) {
      for (const [oldId, newId] of localOldNewMap) {
        oldNewMap.set(oldId, newId)
      }
    } else {
      SyncCommitHandler.doInBatch(() => {
        for (const newId of localOldNewMap.values()) {
          Gantt.relinkTaskDependencies(newId, localOldNewMap)
        }
      })
    }
  }

  private static recreateTaskTemplates(board: Hub): void {
    const templates = board?.data?.taskTemplates
    if (!templates) {
      return
    }

    const newTemplates: DataObjectId[] = []

    const box = Sync.getCurrentSandbox()
    for (const templateId of templates) {
      const hub = box.getHub(templateId)
      if (!hub || hub.deleted) {
        continue
      }

      const id = this.recreateTaskTemplate(hub, board.id)

      newTemplates.push(id)
    }

    board.data.taskTemplates = newTemplates
  }

  private static recreateTaskTemplate(
    template: Hub,
    boardId: DataObjectId
  ): DataObjectId {
    const box = Sync.getCurrentSandbox()
    const hub = new Hub(globals.getCompanyId(), '')
    hub.id = createDataObjectId()
    hub.title = hub.id
    hub.location = taskTemplatesContainerId
    hub.data = Util.clone(template.data)
    hub.description = template.description
    hub.stickers = Util.clone(template.stickers)

    const tags = Object.keys(hub.stickers)
      .map(sId => {
        return Sticker.getById(box, sId as DataObjectId)
      })
      .filter(TypeUtil.isTruthy)

    const filteredIds = filterCustomStickers(tags).map(s => s.id)

    hub.stickers = (Object.keys(hub.stickers) as DataObjectId[]).reduce(
      (acc, stickerId) => {
        if (filteredIds.includes(stickerId)) {
          acc[stickerId] = hub.stickers[stickerId]
        }

        return acc
      },
      {} as {[id: string]: JSONLikeValue}
    )

    const deadlineTag = tags.find(t => t.title === 'Deadline')
    if (deadlineTag) {
      hub.stickers[deadlineTag.id] = ''
    }

    const timerTag = tags.find(t => t.title === 'Timer')
    if (timerTag) {
      const time = hub.stickers[timerTag.id]?.current?.time
      if (time) {
        const entry: TimerStickerEntry = {
          by: globals.currentUserId,
          running: false,
          time,
          timestamp: Time.now()
        }
        hub.stickers[timerTag.id] = {
          current: entry,
          history: [entry]
        }
      } else {
        hub.stickers[timerTag.id] = ''
      }
    }

    const allUsers = box.getAllUsers(false)
    const users = allUsers
      .filter(u => ChatSubscribers.isSubscribed(u.id, template))
      .map(u => u.id)

    hub.data.taskTemplate = {
      id: hub.id,
      boardId: boardId,
      title: template?.data?.taskTemplate?.title || 'not-defined',
      enabled: true,
      subscribeUsers: users
    }

    recreateSubtasksForTaskTemplate(hub)

    box.add(hub, taskTemplatesContainerId, true, false)

    return hub.id
  }

  public static removeColumn(column: HubLike): void {
    const box = Sync.get(column)
    const hub = box.get(column.id) as Hub
    if (hub) {
      box.remove(hub)
      Board.getColumnsOrder(box.get(hub.location) as Hub).cleanDeleted()
      GanttDraft.planEntitySubtree(hub.id, box)
    }
  }

  public static sortColumns(board: HubLike, columns: Hub[]): void {
    const colOrder = Board.getColumnsOrder(board)
    columns.sort((a, b) => colOrder.order(a) - colOrder.order(b))
  }

  public static listColumns(
    board: HubLike,
    opts?: {
      hierarchy?: StorageDepartmentHierarchy
      sorted?: boolean
      noMirrors?: boolean
    }
  ): Hub[] {
    const box = Sync.get(board)
    if (!box) {
      return []
    }
    const calculatedHierarchy =
      opts?.hierarchy ?? DepartmentsClient.getHierarchy(box)
    const res = box.getChildrenHubs(board.id).filter(
      col =>
        PermissionsChecker.checkShow('ignore-departments', col, {
          calculatedHierarchy
        }) &&
        (!opts?.noMirrors || !Column.isMirror(col))
    )
    if (opts?.sorted) {
      Board.sortColumns(board, res)
    }
    return res
  }

  public static listColumnsOrdered(board: HubLike): ColumnHub[] {
    const columns = [...Board.listColumns(board)]
    const order = Board.getColumnsOrder(board)
    const originalColumns = columns.filter(Util.negate(Column.isMirror))

    const mirroredColumns = ArrUtil.flatten<ColumnHub>(
      columns.filter(Column.isMirror).map(Column.getMirroredColumns)
    ).map((g: ColumnHub) => ({...g, ...{isMirrored: true}}) as ColumnHub)

    const sort = (first: ColumnHub, second: ColumnHub): number => {
      return order.order(first) - order.order(second)
    }

    return originalColumns
      .sort(sort)
      .concat(mirroredColumns.sort(sort)) as ColumnHub[]
  }

  public static getNewTasks(board: Hub): Hub[] {
    const box = Sync.get(board)
    const res: Hub[] = []
    box.walkForward(board, (obj, level) => {
      if (obj.deleted || obj.title.startsWith('.')) {
        return false
      }
      if (level === 2) {
        if (SeenBy.isNew(obj, globals.currentUserId)) {
          res.push(obj)
        }
        return false
      }
      return true
    })
    return res
  }

  public static find(board: HubLike): Hub {
    return Sync.get(board).get(board.id) as Hub
  }

  public static editTitle(board: EntitledHubLike, title: string): void {
    if (board.title !== title) {
      const hub = Board.find(board)
      const canEdit = Items.rename(Items.fromHub(hub), title)

      if (canEdit) {
        Sync.get(board).editTitle(hub, title)
      }
    }
  }

  public static columnDropHandler(
    placeholderIndex: number,
    column: Hub,
    boardWas: Hub,
    toBoard: Hub
  ): void {
    const sb = Sync.get(toBoard)

    if (boardWas && toBoard && boardWas.id !== toBoard.id) {
      const proceed =
        Items.move(
          Items.fromHub(column),
          Items.fromHub(boardWas),
          Items.fromHub(toBoard)
        ) && PermissionsChecker.checkMove(column, boardWas, toBoard)
      if (!proceed) {
        sb.moveTreeNode(column, column.location, toBoard.id, true, false)
        setTimeout(() => {
          sb.moveTreeNode(column, column.location, boardWas.id, true, false)
        }, 1000)
        return
      }
    }

    const ord = Board.getColumnsOrder(toBoard)
    ord.checkAndFixEntities()
    if (boardWas && boardWas.id !== toBoard.id) {
      const ordWas = Board.getColumnsOrder(boardWas)
      ordWas.checkAndFixEntities()
    }

    const filtered: Hub[] = Board.listColumns(toBoard).sort(
      (a: Hub, b: Hub) => ord.order(a) - ord.order(b)
    )

    if (filtered.length === 0) {
      ord.handleDrop(0, column)
    } else if (placeholderIndex === 0) {
      let ix = ord.order(filtered[0])
      ix = ix >= 0 ? ix : 0
      ord.handleDrop(ix, column)
    } else {
      const afterTask = filtered[placeholderIndex - 1]
      const afterIx = ord.order(afterTask)
      ord.handleDrop(afterIx + 1, column)
    }

    if (column.location !== toBoard.id) {
      const title = sb.notConflictingTitle(
        column.title,
        column.dataType,
        toBoard.id
      )
      if (title !== column.title) {
        column.title = title
      }
      Board.getColumnsOrder(sb.get(column.location) as Hub).removeEntity(column)
      sb.moveTreeNode(column, column.location, toBoard.id, true, false)
    }
  }

  public static parentProject(board: HubLike): Hub {
    return Sync.parentHub(board)
  }

  public static getOpenedTasksData(board: Hub): TaskData[] {
    const box = Sync.get(board)
    const res: TaskData[] = []
    box.walkForward(board, (obj, level) => {
      if (obj.deleted || obj.title.startsWith('.')) {
        return false
      }
      if (level === 2) {
        res.push(taskDataFromHub(box, obj))
        return false
      }
      return true
    })
    return res.filter(t => t.gtdStage === 'done' || Task.isArchived(t))
  }

  public static createCrmTemplateColumns(box: SandboxTree, board: Hub): void {
    const defaultColumnOptions = {
      color: {hue: 1}
    }
    Board.addColumn(
      Lang.msg.crm.board.inboxColumnTitle,
      board,
      false,
      defaultColumnOptions
    )
    Board.addColumn(
      Lang.msg.crm.board.offerColumnTitle,
      board,
      false,
      defaultColumnOptions
    )
    Board.addColumn(
      Lang.msg.crm.board.paymentColumnTitle,
      board,
      false,
      defaultColumnOptions
    )

    Board.addColumn(Lang.msg.crm.board.succeededColumnTitle, board, false, {
      color: {hue: 5},
      crmSettings: {isSucceededColumn: true}
    })
    Board.addColumn(Lang.msg.crm.board.rejectedColumnTitle, board, false, {
      color: {hue: 2},
      crmSettings: {isRejectedColumn: true}
    })

    board.data.settings ??= {}
    board.data.settings.counterInColumns = true

    const {addToBoard, getByName} = Sticker
    addToBoard(board, getByName(box, 'User'))

    const dealAmountStickerTitle = Lang.msg.crm.dealAmountStickerTitle
    let dealAmountSticker = Sticker.getById(box, crmDealAmountStickerId)

    if (!dealAmountSticker) {
      const controller = Sticker.getController(customStickerTypes[3])
      if (controller) {
        dealAmountSticker = controller.make(box)
        dealAmountSticker.id = crmDealAmountStickerId
        dealAmountSticker.title = dealAmountStickerTitle
        Sticker.add(box, dealAmountSticker)
        Sticker.setIcon(dealAmountSticker, 'ruble')
        dealAmountSticker.commit()
      }
    }

    if (dealAmountSticker.deleted) {
      dealAmountSticker.deleted = false
      dealAmountSticker.commit()
    }

    addToBoard(board, dealAmountSticker)

    board.data.stickersSettings ??= {}
    board.data.stickersSettings[dealAmountSticker.id] = {
      showNumericStickersSum: true,
      required: 'offer'
    }

    const priority =
      getByName(box, langRu.stickerPriority) ||
      getByName(box, langEn.stickerPriority)

    if (priority) {
      addToBoard(board, priority)
    }

    board.commit()
  }
}
